import { cn } from '@/utils/styles';
import type { ProductPack } from '@viome/viome-base/src/api/Strapi/StrapiTypes';
import { formatPackPrice } from '@screens/ShopPage/utils/pricing';

type AddonPackSelectorProps = {
  packs: ProductPack[];
  basePrice: number;
  selectedPack: number;
  onSelect: (pack: number) => void;
  trimDecimal?: boolean;
  className?: string;
};

export const getPackPrices = (basePrice: number, pack: ProductPack) => {
  const originalPrice = basePrice * pack.pack;
  const hasDiscount = pack.discountPercent > 0;
  const discountedPrice = hasDiscount
    ? originalPrice * (1 - pack.discountPercent / 100)
    : originalPrice;
  return { originalPrice, discountedPrice, hasDiscount };
};

export const AddonPackSelector = ({
  packs,
  basePrice,
  selectedPack,
  onSelect,
  trimDecimal,
  className,
}: AddonPackSelectorProps) => {
  if (!packs.length) return null;

  return (
    <div
      className={cn(
        'flex flex-col gap-3 md:flex-row md:items-stretch',
        className,
      )}
      role="radiogroup"
      aria-label="Pack options"
    >
      {packs.map((pack) => {
        const isSelected = selectedPack === pack.pack;
        const { originalPrice, discountedPrice, hasDiscount } = getPackPrices(
          basePrice,
          pack,
        );
        const packLabel =
          pack.pack === 1 ? '1 Pack' : `${pack.pack} Packs`;

        return (
          <button
            key={pack.pack}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => onSelect(pack.pack)}
            className={cn(
              'relative flex flex-1 items-center justify-between gap-3 rounded-xl px-4 py-3.5 text-left transition-colors',
              isSelected
                ? 'border-2 border-[#146464] bg-white'
                : 'border-2 border-transparent bg-[#F3F5F8]',
            )}
            data-testid={`shopAddOn-pack-${pack.pack}`}
          >
            {pack.badge ? (
              <span className="absolute -top-2.5 left-3 rounded-full bg-[#146464] px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white">
                {pack.badge}
              </span>
            ) : null}

            <span
              className={cn(
                'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2',
                isSelected ? 'border-[#146464]' : 'border-[#B9BDC9]',
              )}
              aria-hidden="true"
            >
              {isSelected ? (
                <span className="h-2 w-2 rounded-full bg-[#146464]" />
              ) : null}
            </span>

            <div className="flex flex-col min-w-0 flex-1">
              <span className="typography-body font-medium text-primary">
                {packLabel}
              </span>
              <span className="text-[11px] font-medium uppercase tracking-wide text-tertiary">
                {pack.capsules} {pack.label}
              </span>
            </div>

            <div className="flex shrink-0 items-baseline gap-1.5">
              {hasDiscount ? (
                <span className="typography-body text-tertiary line-through">
                  {formatPackPrice(originalPrice, trimDecimal)}
                </span>
              ) : null}
              <span className="typography-h3 text-[#146464]">
                {formatPackPrice(discountedPrice, trimDecimal)}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
};
